import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Gear, 
  GitBranch,
  Plus,
  Link,
  Trash,
  Copy,
  PencilSimple,
  Play,
  Square,
  BookOpen,
  Database,
  Wrench,
  FileText,
  Phone,
  ChatCircle,
  Envelope,
  Globe,
  Hash
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/text-area';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from '../../lib/utils';
import { ConditionsList } from './ConditionEditor';
import { toast } from 'react-hot-toast';

// Custom SelectItem without checkmark
const CustomSelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-2 text-sm outline-none focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
CustomSelectItem.displayName = "CustomSelectItem";

// Helper function to get color classes for different action types
const getColorClasses = (color) => {
  switch (color) {
    case 'blue':
      return {
        border: 'border-blue-200',
        borderSelected: 'border-blue-400',
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        textSecondary: 'text-blue-700',
        handle: '!bg-blue-500'
      };
    case 'purple':
      return {
        border: 'border-purple-200',
        borderSelected: 'border-purple-400',
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        textSecondary: 'text-purple-700',
        handle: '!bg-purple-500'
      };
    case 'yellow':
      return {
        border: 'border-amber-200',
        borderSelected: 'border-amber-400',
        bg: 'bg-amber-100',
        text: 'text-amber-600',
        textSecondary: 'text-amber-700',
        handle: '!bg-amber-500'
      };
    default:
      return {
        border: 'border-blue-200',
        borderSelected: 'border-blue-400',
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        textSecondary: 'text-blue-700',
        handle: '!bg-blue-500'
      };
  }
};

// Helper function to get action configuration (same as WorkflowFlow.js)
const getActionNodeConfig = (step) => {
  // Get type and subtype directly from step properties first
  let type = step.action_type;
  let subtype = step.action_subtype;

  // If not found, check references for backward compatibility
  if (!type && step.references && step.references.length > 0) {
    const firstReference = step.references[0];
    type = firstReference.reference_type || firstReference.original_type || firstReference.type;
    subtype = firstReference.subtype || firstReference.action_type;
  }

  switch (type) {
    case 'simple_answer':
      return {
        icon: BookOpen,
        color: 'blue',
        label: 'Simple Answer',
        shouldEndFlow: false
      };

    case 'complex_answer':
      if (subtype === 'knowledge') {
        return {
          icon: Database,
          color: 'purple',
          label: 'Knowledge Answer',
          shouldEndFlow: false
        };
      } else if (subtype === 'api') {
        return {
          icon: Globe,
          color: 'purple',
          label: 'API Answer',
          shouldEndFlow: false
        };
      } else {
        return {
          icon: Database,
          color: 'purple',
          label: 'Complex Answer',
          shouldEndFlow: false
        };
      }

    case 'simple_action':
      // Handle both database values and legacy values
      switch (subtype) {
        case 'note_taking':
        case 'note':
          return {
            icon: FileText,
            color: 'yellow',
            label: 'Note Taking',
            shouldEndFlow: false
          };
        case 'transfer_call':
        case 'transfer':
          return {
            icon: Phone,
            color: 'yellow',
            label: 'Transfer Call',
            shouldEndFlow: true
          };
        case 'text_message':
        case 'sms':
          return {
            icon: ChatCircle,
            color: 'yellow',
            label: 'Text Message',
            shouldEndFlow: false
          };
        case 'email':
          return {
            icon: Envelope,
            color: 'yellow',
            label: 'Email',
            shouldEndFlow: false
          };
        case 'api':
          return {
            icon: Globe,
            color: 'yellow',
            label: 'API Action',
            shouldEndFlow: false
          };
        case 'dtmf':
          return {
            icon: Hash,
            color: 'yellow',
            label: 'DTMF',
            shouldEndFlow: false
          };
        default:
          return {
            icon: Wrench,
            color: 'yellow',
            label: 'Simple Action',
            shouldEndFlow: false
          };
      }

    default:
      return {
        icon: Gear,
        color: 'blue',
        label: 'Action Step',
        shouldEndFlow: false
      };
  }
};

const StepEditPanel = ({
  step,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  onDuplicate,
  onAddBrainItemReference,
  onRemoveBrainItemReference,
  onAddStep,
  onRemoveStep,
  availableSteps = [],
  className = ""
}) => {
  const [stepData, setStepData] = useState({
    name: '',
    type: 'action',
    config: {},
    references: [],
    conditions: [],
    ...step
  });

  // Step types configuration - calculated dynamically to update when stepData changes
  const getStepTypeConfig = (type, data) => {
    switch (type) {
      case 'start':
        return {
      icon: Play,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      label: 'Start Step'
        };
      case 'action':
        const actionConfig = getActionNodeConfig(data);
        const colorClasses = getColorClasses(actionConfig.color);
        return {
          icon: actionConfig.icon,
          color: colorClasses.text,
          bgColor: colorClasses.bg.replace('bg-', 'bg-').replace('-100', '-50'),
          borderColor: colorClasses.border,
          label: actionConfig.label
        };
      case 'condition':
        return {
      icon: GitBranch,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      label: 'Condition Step'
        };
      case 'end':
        return {
      icon: Square,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      label: 'End Step'
        };
      default:
        return {
          icon: Gear,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Action Step'
        };
    }
  };

  useEffect(() => {
    if (step) {
      setStepData({
        name: '',
        type: 'action',
        config: {},
        references: [],
        conditions: [],
        ...step
      });
    }
  }, [step]);

  const handleFieldChange = (field, value) => {
    // Prevent changing step type to or from 'start'
    if (field === 'type') {
      if (stepData.type === 'start' && value !== 'start') {
        toast.error("Cannot change the start step type. Every workflow must have exactly one start step.");
        return;
      }
      if (stepData.type !== 'start' && value === 'start') {
        toast.error("Cannot change step to start type. Every workflow must have exactly one start step.");
        return;
      }
    }

    let newStepData = { ...stepData, [field]: value };

    // Auto-naming logic for condition steps only
    if (field === 'config' && stepData.type === 'condition' && value.prompt) {
      // For condition steps, use the prompt as the name
      newStepData.name = value.prompt;
    }

    setStepData(newStepData);
    if (onUpdate) {
      onUpdate(newStepData);
    }
  };

  const handleAddCondition = () => {
    const conditionNumber = (stepData.conditions || []).length + 1;
    const newCondition = {
      id: `cond-${Date.now()}`,
      name: `Branch ${conditionNumber}`,
      condition_type: 'contains',
      variable_path: 'user_input',
      condition_operator: 'contains',
      expected_value: '',
      target_step_id: '',
      condition_order: conditionNumber,
      is_active: true
    };

    const newConditions = [...(stepData.conditions || []), newCondition];
    handleFieldChange('conditions', newConditions);
  };

  const handleUpdateCondition = (index, updatedCondition) => {
    const newConditions = [...(stepData.conditions || [])];
    newConditions[index] = updatedCondition;
    handleFieldChange('conditions', newConditions);
  };

  const handleDeleteCondition = (index) => {
    const newConditions = [...(stepData.conditions || [])];
    newConditions.splice(index, 1);
    handleFieldChange('conditions', newConditions);
  };

  if (!isOpen || !step) {
    return null;
  }

  const currentConfig = getStepTypeConfig(stepData.type, stepData);
  const Icon = currentConfig.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 z-[10006] overflow-y-auto ${className}`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${currentConfig.bgColor} ${currentConfig.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${currentConfig.bgColor}`}>
                <Icon className={`h-4 w-4 ${currentConfig.color}`} />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{currentConfig.label}</h3>
                <p className="text-sm text-gray-500">Configure step settings</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onDuplicate && stepData.type !== 'start' && (
                <button
                  onClick={() => onDuplicate(stepData)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                  title="Duplicate Step"
                >
                  <Copy className="h-4 w-4" />
                </button>
              )}
              {onDelete && stepData.type !== 'start' && (
                <button
                  onClick={() => onDelete(stepData.id)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                  title="Delete Step"
                >
                  <Trash className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Step Configuration */}
          {(stepData.type === 'start' || stepData.type === 'end') && (
          <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Step Name</h4>
                <Input
                  value={stepData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Enter step name"
                className="text-base border-gray-200 rounded-xl p-4 focus:border-gray-400 focus:ring-0"
                />
              </div>
            )}

            {stepData.type === 'condition' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Step Name</h4>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <p className="text-sm text-orange-700 font-medium">
                  {stepData.name || stepData.config?.prompt || 'Auto-generated from question'}
                </p>
              </div>
              </div>
            )}

            {stepData.type === 'action' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Step Name</h4>
              <div className={`${currentConfig.bgColor} rounded-xl p-4 border ${currentConfig.borderColor}`}>
                <p className={`text-sm font-medium ${currentConfig.color.replace('text-', 'text-').replace('-600', '-700')}`}>
                  {stepData.name || 'Auto-generated from connected item'}
                </p>
              </div>
              </div>
            )}

          {/* Step Type Selector */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Step Type</h4>
              <Select
                value={stepData.type}
                onValueChange={(value) => handleFieldChange('type', value)}
              >
              <SelectTrigger className="text-base border-gray-200 rounded-xl p-4 focus:border-gray-400 focus:ring-0 h-auto">
                  <SelectValue />
                </SelectTrigger>
              <SelectContent className="rounded-xl border-gray-200 z-[10007]">
                <CustomSelectItem value="action" className="p-3 rounded-lg pl-3">
                    <div className="flex items-center">
                    <Gear className="h-5 w-5 text-blue-600 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">Action</div>
                      <div className="text-xs text-gray-500">Execute a brain item</div>
                    </div>
                    </div>
                  </CustomSelectItem>
                <CustomSelectItem value="condition" className="p-3 rounded-lg pl-3">
                    <div className="flex items-center">
                    <GitBranch className="h-5 w-5 text-orange-600 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">Condition</div>
                      <div className="text-xs text-gray-500">Ask user a question</div>
                    </div>
                    </div>
                  </CustomSelectItem>
                <CustomSelectItem value="end" className="p-3 rounded-lg pl-3">
                    <div className="flex items-center">
                    <Square className="h-5 w-5 text-purple-600 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">End</div>
                      <div className="text-xs text-gray-500">Finish workflow</div>
                    </div>
                    </div>
                  </CustomSelectItem>
                </SelectContent>
              </Select>
          </div>

          {/* Brain Item Reference (for action steps) */}
          {stepData.type === 'action' && (
            <div className="space-y-6">
              {stepData.references && stepData.references.length > 0 ? (
                <div className="space-y-4">
                  {stepData.references.slice(0, 1).map((reference) => {
                    // Get the reference config for proper styling
                    const refConfig = getActionNodeConfig({ 
                      action_type: reference.reference_type, 
                      action_subtype: reference.subtype,
                      references: [reference]
                    });
                    const refColorClasses = getColorClasses(refConfig.color);

                    return (
                      <div key={reference.id} className="group">
                        {/* Clean header */}
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Connected Item</h4>
                      <button
                        onClick={() => onRemoveBrainItemReference && onRemoveBrainItemReference(stepData.id, reference.id)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-all duration-200"
                            title="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                        {/* Beautiful main card */}
                        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                          <div className="flex items-start space-x-4">
                            {/* Icon */}
                            <div className={`w-12 h-12 ${refColorClasses.bg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                              {React.createElement(refConfig.icon, { className: `w-6 h-6 ${refColorClasses.text}` })}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">{reference.name}</h3>

                              {/* Description */}
                              {reference.description && (
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{reference.description}</p>
                              )}

                              {/* Clean metadata */}
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className={`${refColorClasses.text} font-medium`}>{refConfig.label}</span>
                                <span>•</span>
                                <span>{reference.category_name || 'Uncategorized'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Clean header */}
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Connected Item</h4>
                  </div>

                  {/* Elegant empty state */}
                  <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center hover:border-gray-300 hover:bg-gray-100/50 transition-all duration-300">
                    <div className={`w-16 h-16 ${currentConfig.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                      {React.createElement(currentConfig.icon, { className: `w-8 h-8 ${currentConfig.color}` })}
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Connect a Brain Item</h3>
                    <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                      Choose what this step should do when executed in your workflow.
                    </p>
                    <Button
                      onClick={() => {
                        if (onAddBrainItemReference) {
                          onAddBrainItemReference(stepData.id);
                        } else {
                          console.error('❌ onAddBrainItemReference is not defined');
                        }
                      }}
                      className={`px-6 py-3 ${currentConfig.color} bg-white border-2 ${currentConfig.borderColor} hover:${currentConfig.bgColor} font-medium rounded-xl transition-all duration-200`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Browse Items
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Conditions (for condition steps) */}
          {stepData.type === 'condition' && (
            <div className="space-y-4">
              {/* Prompt Configuration */}
              <div className={`p-4 rounded-lg ${currentConfig.bgColor} ${currentConfig.borderColor} border`}>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <GitBranch className="h-4 w-4 mr-2" />
                  User Prompt Configuration
                </h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Question/Prompt <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={stepData.config?.prompt || ''}
                      onChange={(e) => handleFieldChange('config', { 
                        ...stepData.config, 
                        prompt: e.target.value 
                      })}
                      placeholder="What question should we ask the user? (e.g., 'What type of issue are you experiencing?')"
                      className="text-sm"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Prompt Type
                    </label>
                    <Select
                      value={stepData.config?.prompt_type || 'open_text'}
                      onValueChange={(value) => handleFieldChange('config', { 
                        ...stepData.config, 
                        prompt_type: value 
                      })}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10007]">
                        <SelectItem value="open_text">Open Text</SelectItem>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {stepData.config?.prompt_type === 'multiple_choice' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Answer Options
                      </label>
                      <div className="space-y-2">
                        {(stepData.config?.options || []).map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...(stepData.config?.options || [])];
                                newOptions[index] = e.target.value;
                                handleFieldChange('config', { 
                                  ...stepData.config, 
                                  options: newOptions 
                                });
                              }}
                              placeholder={`Option ${index + 1}`}
                              className="text-sm flex-1"
                            />
                            <button
                              onClick={() => {
                                const newOptions = [...(stepData.config?.options || [])];
                                newOptions.splice(index, 1);
                                handleFieldChange('config', { 
                                  ...stepData.config, 
                                  options: newOptions 
                                });
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <Button
                          onClick={() => {
                            const newOptions = [...(stepData.config?.options || []), 'New Option'];
                            handleFieldChange('config', { 
                              ...stepData.config, 
                              options: newOptions 
                            });
                          }}
                          size="sm"
                          className="px-3 py-1 bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-300"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Conditions Configuration */}
              <div className={`p-4 rounded-lg ${currentConfig.bgColor} ${currentConfig.borderColor} border`}>
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <GitBranch className="h-4 w-4 mr-2" />
                  Decision Branches
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  Define how user responses should route to different steps
                </p>
                <ConditionsList
                  conditions={stepData.conditions || []}
                  onAddCondition={handleAddCondition}
                  onUpdateCondition={handleUpdateCondition}
                  onDeleteCondition={handleDeleteCondition}
                  availableSteps={availableSteps.filter(s => s.id !== stepData.id)}
                  promptConfig={stepData.config || {}}
                  onAddStep={onAddStep}
                  onRemoveStep={onRemoveStep}
                  parentStepId={stepData.id}
                />
              </div>
            </div>
          )}

          {/* Step Configuration */}
          {stepData.type === 'start' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Start Configuration</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Welcome Message (Optional)
                </label>
                <Textarea
                  value={stepData.config?.welcome_message || ''}
                  onChange={(e) => handleFieldChange('config', { 
                    ...stepData.config, 
                    welcome_message: e.target.value 
                  })}
                  placeholder="Enter welcome message for users"
                  className="text-sm"
                  rows={3}
                />
              </div>
            </div>
          )}

          {stepData.type === 'end' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">End Configuration</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Closing Message (Optional)
                </label>
                <Textarea
                  value={stepData.config?.closing_message || ''}
                  onChange={(e) => handleFieldChange('config', { 
                    ...stepData.config, 
                    closing_message: e.target.value 
                  })}
                  placeholder="Enter closing message for users"
                  className="text-sm"
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StepEditPanel; 